import { Download, Loader2 } from "lucide-react";

// Two stacked components: a colored banner explaining the current gallery phase
// (proofing vs delivery), and a download bar that appears once delivered — gated by
// the optional download password.

export function PhaseBanner({ isDelivered, includedEdits, extraPrice }) {
  if (!isDelivered) {
    return (
      <div className="bg-amber-50 border border-amber-200 px-5 py-4 mb-8 text-sm" data-testid="gallery-phase-proofing">
        <div className="font-medium text-amber-900 mb-1">Step 1 · Pick your favorites for editing</div>
        <div className="text-amber-800">
          Tap the heart on the photos you&rsquo;d like edited.{" "}
          {includedEdits > 0 && (
            <>
              Your package includes <strong>{includedEdits}</strong> complimentary edit{includedEdits > 1 ? "s" : ""};
              additional edits are <strong>${Number(extraPrice).toFixed(2)}</strong> each.
            </>
          )}{" "}
          You can leave a per-photo note (e.g. "crop tighter") by clicking the photo.
        </div>
      </div>
    );
  }
  return (
    <div className="bg-emerald-50 border border-emerald-200 px-5 py-4 mb-8 text-sm" data-testid="gallery-phase-delivery">
      <div className="font-medium text-emerald-900 mb-1">Your edited photos are ready</div>
      <div className="text-emerald-800">Use your download password to unlock high-res downloads.</div>
    </div>
  );
}

export function DownloadBar({
  requiresDlPw,
  downloadPassword,
  setDownloadPassword,
  onVerify,
  verifyingDl,
  zipping,
  onZip,
}) {
  return (
    <div className="mb-8" data-testid="gallery-download-bar">
      {requiresDlPw ? (
        <form onSubmit={onVerify} className="max-w-md mx-auto flex gap-2">
          <input
            type="password"
            value={downloadPassword}
            onChange={(e) => setDownloadPassword(e.target.value)}
            placeholder="Download password"
            className="flex-1 bg-transparent border border-stone-300 px-3 py-2 outline-none focus:border-stone-900"
            data-testid="download-password-input"
          />
          <button
            type="submit"
            disabled={!downloadPassword || verifyingDl}
            className="px-4 h-10 bg-stone-900 text-stone-50 text-xs uppercase tracking-[0.18em] hover:bg-stone-800 disabled:opacity-50"
            data-testid="unlock-downloads-btn"
          >
            {verifyingDl ? "…" : "Unlock"}
          </button>
        </form>
      ) : (
        <div className="text-center">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => onZip("web")}
              disabled={!!zipping}
              className="inline-flex items-center gap-2 px-6 h-12 bg-stone-900 text-stone-50 text-xs uppercase tracking-[0.22em] hover:bg-stone-800 disabled:opacity-50"
              data-testid="download-all-web-btn"
            >
              {zipping === "web" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {zipping === "web" ? "Downloading…" : "Download all · Web size"}
            </button>
            <button
              onClick={() => onZip("original")}
              disabled={!!zipping}
              className="inline-flex items-center gap-2 px-6 h-12 border border-stone-900 text-stone-900 text-xs uppercase tracking-[0.22em] hover:bg-stone-900 hover:text-stone-50 disabled:opacity-50 transition-colors"
              data-testid="download-all-hires-btn"
            >
              {zipping === "original" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {zipping === "original" ? "Downloading…" : "Download all · High resolution"}
            </button>
          </div>
          <p className="text-xs text-stone-500 mt-2">
            Web size downloads fast and is great for phones &amp; sharing. High resolution is the full original files.
            Or click any photo to download it individually.
          </p>
        </div>
      )}
    </div>
  );
}

function formatBytes(n) {
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Fixed bottom overlay showing live download progress (percent when the size is
// known, running byte count for streamed zips).
export function DownloadProgress({ progress }) {
  if (!progress) return null;
  const { label, pct, bytes } = progress;
  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[92%] max-w-md bg-stone-900 text-stone-50 shadow-xl px-5 py-4"
      data-testid="download-progress-bar"
    >
      <div className="flex items-center justify-between gap-3 text-xs mb-2">
        <span className="truncate">Downloading {label}</span>
        <span className="shrink-0 tabular-nums" data-testid="download-progress-value">
          {pct != null ? `${pct}%` : formatBytes(bytes)}
        </span>
      </div>
      <div className="h-1.5 bg-stone-700 overflow-hidden">
        {pct != null ? (
          <div className="h-full bg-emerald-400 transition-[width] duration-200" style={{ width: `${pct}%` }} />
        ) : (
          <div className="h-full w-1/3 bg-emerald-400 animate-[dl-slide_1.2s_ease-in-out_infinite]" />
        )}
      </div>
      <style>{`@keyframes dl-slide { 0% { margin-left: -33%; } 100% { margin-left: 100%; } }`}</style>
    </div>
  );
}
